import {secondsToHms} from "components/pages/users-statistics-dashboard/UsersStatisticsDashboard";
import * as d3 from "d3";
import React, {useEffect, useRef} from "react";

const CHART_HEIGHT = 350;
const CHART_MARGIN = {top: 0, right: 20, bottom: 20, left: 150};

interface IDataItem {
    from: string;
    to: string;
    username: string;
    companyRef: string;
}

interface IUserStatisticsGanttChartProps {
    data: IDataItem[];
    selectedType: string;
    companyFilter: string[] | undefined;
}

const parseDate = (dateString: string | null) => {
    if (dateString === null) {
        return new Date(); // or any default date
    }
    return d3.timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(dateString);
};

const UserStatisticsGanttChart = ({data, selectedType, companyFilter}: IUserStatisticsGanttChartProps) => {
    const svgRef = useRef(null);
    const tooltipRef = useRef(null); // Ref for the tooltip div

    const filterData = (data, companyFilter, selectedType) => {
        let filteredData = [...data];
        if (companyFilter && companyFilter.length > 0) {
            filteredData = filteredData.filter(d => companyFilter.includes(d.companyRef));
        }
        if (selectedType) {
            filteredData = filteredData.map(d => ({...d, username: d[selectedType]}));
        }
        return filteredData;
    };

    useEffect(() => {
        const filteredData = filterData(data, companyFilter, selectedType);
        // Set up chart dimensions

        let width = svgRef.current.parentElement.offsetWidth - CHART_MARGIN.left - CHART_MARGIN.right;
        const height = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;

        // Create SVG container
        const svgContainer = d3
            .select(svgRef.current)
            .attr("width", width + CHART_MARGIN.left + CHART_MARGIN.right)
            .attr("height", height + CHART_MARGIN.top + CHART_MARGIN.bottom);

        const svg = svgContainer
            .append("g")
            .attr("transform", `translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`);


        let xScale = d3.scaleTime().domain([new Date(), new Date()]).range([0, width]);
        let minDate = d3.min(filteredData, (d) => parseDate(d.from));
        let maxDate = d3.max(filteredData, (d) => parseDate(d.to));
        if (minDate === undefined || maxDate === undefined) {
            // Handle the case where minDate or maxDate is undefined
        } else {
            xScale = d3.scaleTime().domain([minDate, maxDate]).range([0, width]);
        }

        const yScale = d3
            .scaleBand()
            .domain(filteredData.map((d) => d.username))
            .range([0, height])
            .padding(0.1);

        // Define color scale
        const colorScale = d3.scaleOrdinal(d3.schemeCategory10).domain(filteredData.map((d) => d.username));

        // Draw Gantt bars
        const bars = svg
            .selectAll("rect")
            .data(filteredData)
            .enter()
            .append("rect")
            .attr("x", (d) => xScale(parseDate(d.from) as any))
            .attr("y", (d) => yScale(d.username) || 0) // Ensure it always returns a number
            .attr("width", (d) => xScale(parseDate(d.to) as any) - xScale(parseDate(d.from) as any))
            .attr("height", yScale.bandwidth())
            .attr("fill", (d) => colorScale(d.username))
            .on("mouseover", function (event, d) { // Add mouseover event
                const sessionTime = (parseDate(d.to) - parseDate(d.from)) / 1000; // Convert to seconds
                const sessionTimeFormat = secondsToHms(sessionTime); // Format to HH:mm:ss
                const startTimeFormat = d3.timeFormat("%H:%M:%S")(parseDate(d.from));
                const endTimeFormat = d3.timeFormat("%H:%M:%S")(parseDate(d.to));
                d3.select(tooltipRef.current)
                    .style("left", event.pageX + "px")
                    .style("top", event.pageY + "px")
                    .style("opacity", 1)
                    .html(`User: ${d.username}<br>Start: ${startTimeFormat}<br>End: ${endTimeFormat}<br>Session Time: ${sessionTimeFormat}`);
            })
            .on("mouseout", function () { // Add mouseout event
                d3.select(tooltipRef.current)
                    .style("opacity", 0);
            });

        // Draw white background to the left of Y-axis
        svg.append("rect")
            .attr("x", 0 - CHART_MARGIN.left)
            .attr("y", 0)
            .attr("width", CHART_MARGIN.left)
            .attr("height", height + CHART_MARGIN.bottom)
            .attr("fill", "white");

        // Draw axes
        const xAxis = d3.axisBottom(xScale);
        const yAxis = d3.axisLeft(yScale);


        svg.append("g").attr("transform", `translate(0,${height})`).call(xAxis as any);
        svg.append("g").call(yAxis as any);


        let brush = d3.brushX()
            .extent([[0, 0], [width, height]])
            .on("end", brushed);


        svg.append("g")
            .attr("class", "brush")
            .call(brush);


        function brushed(event) {
            if (event.selection) {
                let extent = event.selection;

                // Update xScale domain based on selected area
                xScale.domain([xScale.invert(extent[0]), xScale.invert(extent[1])]);

                // Redraw the chart
                svg.select(".x.axis").call(xAxis);
                bars.attr("x", d => xScale(parseDate(d.from) as Date))
                    .attr("width", d => xScale(parseDate(d.to) as Date) - xScale(parseDate(d.from) as Date));

                // Clear the brush selection
                svg.select(".brush").call(brush.move, null);
            }
        }


        // Cleanup on component unmount
        return () => {
            svg.selectAll("*").remove();
        };
    }, [data, selectedType, companyFilter]);


    return (
        <div>
            <svg ref={svgRef}></svg>
            <div ref={tooltipRef} style={{
                position: "absolute",
                zIndex: 10,
                opacity: 0,
                backgroundColor: "white",
                color: "black",
                padding: "2px",
                borderRadius: "2px",
                pointerEvents: "none", // Ignore pointer events to prevent the tooltip from interfering with the chart
                transform: "translate(0, 24px)" // Position the tooltip above the cursor
            }}>
            </div>
        </div>
    );

};

export default UserStatisticsGanttChart;